import * as React from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/joy";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";

import { getAdminReadingNote, ReadingNote, updateAdminReadingNote } from "../api";
import AdminReadingNoteForm, { ReadingNoteFormValues } from "../components/Admin/AdminReadingNoteForm";
import PageHero from "../components/common/PageHero";

export default function AdminReadingNoteEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [note, setNote] = React.useState<ReadingNote | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!id) {
        setNote(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const data = await getAdminReadingNote(id);
        if (!cancelled) {
          setNote(data ?? null);
        }
      } catch (error) {
        if (!cancelled) {
          setNote(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleUpdate = async (data: ReadingNoteFormValues) => {
    if (!id || saving) return;
    setSaving(true);
    try {
      await updateAdminReadingNote(id, data);
      navigate(`/articles/${id}`);
    } finally {
      setSaving(false);
    }
  };

  const heroDescription = note
    ? `Refine this reflection before devotees explore it in the reading library. Last updated ${new Date(
        note.createdAt,
      ).toLocaleString()}.`
    : "Update the article content to keep the library filled with fresh guidance.";

  let content: React.ReactNode;

  if (loading) {
    content = (
      <Card
        variant="outlined"
        sx={{ borderRadius: "xl", p: { xs: 2.5, md: 3 }, display: "flex", justifyContent: "center" }}
      >
        <Stack spacing={1} alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size="sm" />
          <Typography level="body-sm" sx={{ color: "text.secondary" }}>
            Loading article…
          </Typography>
        </Stack>
      </Card>
    );
  } else if (!note) {
    content = (
      <Card variant="outlined" sx={{ borderRadius: "xl", p: { xs: 2.5, md: 3 } }}>
        <Stack spacing={1.5} alignItems="center" textAlign="center">
          <Avatar variant="soft" color="primary" sx={{ width: 56, height: 56, borderRadius: "lg" }}>
            <MenuBookRoundedIcon />
          </Avatar>
          <Typography level="title-sm" sx={{ fontWeight: 600 }}>
            Article not found
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 360 }}>
            It may have been unpublished or shared under a different link. Return to the library to manage other reflections.
          </Typography>
          <Button
            component={RouterLink}
            to="/articles"
            variant="outlined"
            color="neutral"
            startDecorator={<ArrowBackRoundedIcon />}
          >
            Back to library
          </Button>
        </Stack>
      </Card>
    );
  } else {
    content = (
      <Card variant="outlined" sx={{ borderRadius: "xl", p: { xs: 2.5, md: 3 }, gap: 2.5 }}>
        <AdminReadingNoteForm
          initial={note}
          onSubmit={handleUpdate}
          submitLabel="Save changes"
          title="Update devotional article"
          loading={saving}
        />
      </Card>
    );
  }

  return (
    <Box>
      <PageHero
        eyebrow="Sacred study"
        title="Edit article"
        description={heroDescription}
        actions={
          <Button
            component={RouterLink}
            to={note ? `/articles/${note.id}` : "/articles"}
            variant="outlined"
            color="neutral"
            startDecorator={<ArrowBackRoundedIcon />}
          >
            {note ? "View article" : "Back to library"}
          </Button>
        }
      />

      {content}
    </Box>
  );
}
